import React, { useEffect, useMemo, useState } from "react";
import {
  useTranslate,
  useShow,
  IResourceComponentsProps,
} from "@pankod/refine-core";
import { Show, DateField } from "@pankod/refine-antd";
import { Descriptions, Tag, Table, Button, Empty, Tooltip } from "antd";
import { PlayCircleOutlined } from "@ant-design/icons";
import {
  IQuizAICreation,
  IQuizAICreationItem,
  QuizAICreationStatus,
} from "interfaces";
import ReviewModal from "./reviewModal";

const STATUS_COLORS: Record<QuizAICreationStatus, string> = {
  pending: "default",
  processing: "processing",
  completed: "success",
  failed: "error",
};

const QUIZ_STATUS_COLORS: Record<string, string> = {
  enabled: "success",
  disabled: "default",
  in_review: "processing",
  for_review: "warning",
};

const BASE_PATH = (process.env.REACT_APP_BASE_PATH || "/").replace(/\/$/, "");

export const QuizAICreationShow: React.FC<IResourceComponentsProps> = () => {
  const t = useTranslate();
  const { queryResult } = useShow<IQuizAICreation>();
  const { data, isLoading, refetch } = queryResult;
  const record = data?.data;

  const status = record?.status;
  const [reviewVisible, setReviewVisible] = useState(false);

  useEffect(() => {
    if (status !== "pending" && status !== "processing") return;
    const interval = setInterval(() => {
      refetch();
    }, 5000);
    return () => clearInterval(interval);
  }, [status, refetch]);

  const reviewableCount = useMemo(
    () =>
      (record?.quizzes || []).filter((q) => q.status === "for_review").length,
    [record?.quizzes]
  );

  const handleReviewClose = () => {
    setReviewVisible(false);
    refetch();
  };

  const reviewButton = (
    <Button
      key="review"
      type="primary"
      icon={<PlayCircleOutlined />}
      disabled={reviewableCount === 0 || status !== "completed"}
      onClick={() => setReviewVisible(true)}
    >
      {t("quiz-ai-creation.review.button")} ({reviewableCount})
    </Button>
  );

  return (
    <Show
      isLoading={isLoading}
      title={t("quiz-ai-creation.title")}
      headerProps={{
        extra: [
          reviewableCount === 0 && status === "completed" ? (
            <Tooltip
              key="review-tip"
              title={t("quiz-ai-creation.review.button_disabled_tooltip")}
            >
              {reviewButton}
            </Tooltip>
          ) : (
            reviewButton
          ),
        ],
      }}
    >
      {record && (
        <>
          <Descriptions bordered size="small" column={2} style={{ marginBottom: 24 }}>
            <Descriptions.Item label={t("quiz-ai-creation.fields.status")}>
              <Tag color={STATUS_COLORS[record.status]}>
                {t(`quiz-ai-creation.status.${record.status}`)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t("quiz-ai-creation.fields.quizzes_created")}>
              {record.quizzes_created}
            </Descriptions.Item>
            <Descriptions.Item label={t("quiz-ai-creation.fields.name")}>
              {record.name}
            </Descriptions.Item>
            <Descriptions.Item label={t("quiz-ai-creation.fields.lang")}>
              <Tag>{record.lang}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t("quiz-ai-creation.fields.url")} span={2}>
              <a href={record.url} target="_blank" rel="noopener noreferrer">
                {record.url}
              </a>
            </Descriptions.Item>
            <Descriptions.Item
              label={t("quiz-ai-creation.show.view_all_in_quiz_list_label")}
              span={2}
            >
              <a
                href={`${BASE_PATH}/quiz?keywords=ai_created_${record.id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("quiz-ai-creation.show.view_all_in_quiz_list")}
              </a>
            </Descriptions.Item>
            <Descriptions.Item label={t("quiz-ai-creation.fields.quiz_category_name")}>
              {record.quiz_category_name}
            </Descriptions.Item>
            <Descriptions.Item label={t("quiz-ai-creation.fields.model_string")}>
              {record.model_string}
            </Descriptions.Item>
            {record.description && (
              <Descriptions.Item label={t("quiz-ai-creation.fields.description")} span={2}>
                {record.description}
              </Descriptions.Item>
            )}
            {record.status === "failed" && record.error_message && (
              <Descriptions.Item label={t("quiz-ai-creation.fields.error_message")} span={2}>
                <span style={{ color: "#ff4d4f" }}>{record.error_message}</span>
              </Descriptions.Item>
            )}
            <Descriptions.Item label={t("quiz-ai-creation.fields.created_at")} span={2}>
              <DateField value={record.created_at} format="LLL" />
            </Descriptions.Item>
          </Descriptions>

          <h3 style={{ marginBottom: 16 }}>
            {t("quiz-ai-creation.show.generated_quizzes")}
          </h3>
          {record.quizzes && record.quizzes.length > 0 ? (
            <Table
              dataSource={record.quizzes}
              rowKey="quiz_id"
              pagination={false}
              size="small"
            >
              <Table.Column
                dataIndex="question"
                title={t("quiz.fields.question")}
                render={(value: string, item: IQuizAICreationItem) => (
                  <Button
                    type="link"
                    style={{
                      padding: 0,
                      whiteSpace: "normal",
                      textAlign: "left",
                      height: "auto",
                    }}
                    onClick={() =>
                      window.open(
                        `${BASE_PATH}/quiz/show/${item.quiz_id}`,
                        "_blank"
                      )
                    }
                  >
                    {value}
                  </Button>
                )}
              />
              <Table.Column
                dataIndex="status"
                title={t("quiz.fields.status")}
                width={140}
                render={(value: string) => (
                  <Tag color={QUIZ_STATUS_COLORS[value] ?? "default"}>
                    {value}
                  </Tag>
                )}
              />
              <Table.Column
                dataIndex="level"
                title={t("quiz.fields.level") || "Level"}
                width={80}
              />
            </Table>
          ) : (
            <Empty description={t("quiz-ai-creation.show.no_quizzes")} />
          )}

          <ReviewModal
            visible={reviewVisible}
            creation={record}
            onClose={handleReviewClose}
          />
        </>
      )}
    </Show>
  );
};
